/**
 * Sprint 1 — Layered guidance / proximity reveal / "the neighbourhood whispers"
 * / recognised serendipity.
 *
 * All client-side, offline-first, privacy-first. No network. Coordinates are used
 * only in memory for distance maths and are never transmitted, persisted as a
 * track, or logged.
 *
 * ── POI identity (read this, Érico) ─────────────────────────────────────────
 * POIs in public/TRIP/{lang}/*.json have NO id field — they are identified by
 * their `name`. poiSlug() derives a stable id from the name.
 *
 *   EDITORIAL RULES:
 *   • Author the optional `reveal { quote, quoteLang, secret, note }` and
 *     `hidden: true` fields directly on the POI object (the one with `name`,
 *     `lat`, `lng`). No id needs to be written by hand.
 *   • The POI `name` IS its identity. Renaming a POI changes its slug and the
 *     user loses its revealed/surfaced state for that trip. Rename with care.
 *   • Keep the same `name` across the en/es/fr files for a POI so its identity
 *     is consistent regardless of UI language. (`localName` may differ freely.)
 */

// Distances in metres. Confirmed by Érico.
export const PROXIMITY_RADIUS_M = 80;        // inside → mark visited + reveal layer
export const WHISPER_RADIUS_M = 150;         // inside → surface a `hidden` spot
export const SERENDIPITY_THRESHOLD_M = 250;  // memory farther than this from EVERY planned POI → off-route

export const GEO_OPTIONS = { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 };

/** Stable id derived from a POI name (accent-insensitive slug). */
export function poiSlug(name) {
  return String(name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip accents
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Haversine distance in METRES. */
export function haversineM(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Flatten every geolocated POI of a loaded city into a uniform list.
 * Tolerates absence of reveal/hidden/coords with elegance.
 */
export function collectCityPois(cityData) {
  if (!cityData) return [];
  const groups = [
    ['culturalAttractions', 'cultural'],
    ['monuments', 'monuments'],
    ['localActivities', 'activities'],
    ['traditionalLocals', 'locals'],
    ['typicalFoodsCity', 'food'],
    ['typicalFoodsCountry', 'food'],
  ];
  const out = [];
  for (const [key, category] of groups) {
    for (const p of (cityData[key] || [])) {
      if (typeof p.lat !== 'number' || typeof p.lng !== 'number') continue; // no coords → can't do proximity
      out.push({
        id: poiSlug(p.name),
        name: p.name,
        lat: p.lat,
        lng: p.lng,
        category,
        reveal: p.reveal || null,
        hidden: p.hidden === true,
      });
    }
  }
  return out;
}
